import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown, Trophy, LogOut, Settings, Lock, CheckCircle2, Timer,
  School as SchoolIcon, Flag, UserCircle2, Sparkles, Download, Share2, Bot,
  ChevronDown, ChevronRight, Instagram, Search, X, ListFilter, Compass, Swords,
  Loader2,
} from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { NATIONALITIES } from "@/lib/nationalities";
import { AvatarPreview } from "@/lib/avatarBuilder";
import EliminatoriasProfileDialog from "./EliminatoriasProfileDialog";
import { fireVictoryConfetti } from "@/components/gameplay/celebrations";

/** Protected: only role="elim_user" can see this. */
export default function EliminatoriasLobby() {
  const { auth, logout, patchExtra } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();

  const [me, setMe] = useState(null);
  const [progress, setProgress] = useState(null);
  const [items, setItems] = useState([]); // active eliminatorias enriched with game
  const [loading, setLoading] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    if (!auth || auth.role !== "elim_user") {
      nav("/eliminatorias", { replace: true });
      return;
    }
    (async () => {
      try {
        const [meRes, progRes, listRes] = await Promise.all([
          api.get("/auth/eliminatoria/me"),
          api.get("/auth/eliminatoria/me/progress"),
          api.get(`/eliminatorias/public?lang=${lang}`),
        ]);
        setMe(meRes.data);
        // Sync the elim user's profile data into sessionStorage so the GamePlay
        // page (which reads from sessionStorage) uses the right avatar, name
        // and school.
        if (meRes.data?.avatar_cfg) {
          sessionStorage.setItem("player_avatar_cfg", JSON.stringify(meRes.data.avatar_cfg));
        }
        if (meRes.data?.name) sessionStorage.setItem("player_name", meRes.data.name);
        if (meRes.data?.school) sessionStorage.setItem("player_school", meRes.data.school);
        if (meRes.data?.nationality) sessionStorage.setItem("player_nationality", meRes.data.nationality);
        setProgress(progRes.data);
        setItems(listRes.data || []);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          logout();
          nav("/eliminatorias", { replace: true });
          return;
        }
        toast.error(t("elim_lobby_load_error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [auth, logout, nav, lang]);

  // Group eliminatorias by game, then break each game into one section per tier
  // (or a single "default" section if the game has no tiers defined).
  const groupedByGameTier = useMemo(() => {
    // First group elims by game.
    const gameMap = new Map();
    for (const e of items) {
      const g = e.game;
      if (!g) continue;
      if (!gameMap.has(g.id)) gameMap.set(g.id, { game: g, elims: [] });
      gameMap.get(g.id).elims.push(e);
    }
    // Build a flat list of (game, tier) sections, one entry per tier (or one if no tiers).
    const sections = [];
    for (const { game, elims } of gameMap.values()) {
      const sortedElims = [...elims].sort((a, b) => (a.order || 0) - (b.order || 0));
      const tiers = Array.isArray(game.tiers) ? game.tiers : [];
      if (tiers.length === 0) {
        sections.push({
          key: `${game.id}|none`,
          game,
          tier: null,
          elims: sortedElims,
        });
      } else {
        for (const t of tiers) {
          sections.push({
            key: `${game.id}|${t.id}`,
            game,
            tier: t,
            elims: sortedElims,
          });
        }
      }
    }
    return sections;
  }, [items]);

  // Tier-aware progress lookup ({elim_id, tier_id} → passed/attempts/wins/losses).
  const progressIndex = useMemo(() => {
    const passed = new Set();
    const attemptsByKey = new Map();
    const winsByKey = new Map();
    const lossesByKey = new Map();
    const avgTimesByKey = new Map();
    const passedTier = progress?.passed_eliminatoria_tier || [];
    for (const p of passedTier) {
      passed.add(`${p.eliminatoria_id}|${p.tier_id || ""}`);
    }
    const counts = progress?.attempts_count_by_eliminatoria_tier || {};
    for (const [k, v] of Object.entries(counts)) {
      attemptsByKey.set(k, v);
    }
    for (const [k, v] of Object.entries(progress?.wins_count_by_eliminatoria_tier || {})) {
      winsByKey.set(k, v);
    }
    for (const [k, v] of Object.entries(progress?.losses_count_by_eliminatoria_tier || {})) {
      lossesByKey.set(k, v);
    }
    for (const [k, v] of Object.entries(progress?.avg_times_by_eliminatoria_tier || {})) {
      avgTimesByKey.set(k, Array.isArray(v) ? v : []);
    }
    const avgStateByKey = new Map();
    for (const [k, v] of Object.entries(progress?.avg_state_by_eliminatoria_tier || {})) {
      avgStateByKey.set(k, v);
    }
    const wvlStateByKey = new Map();
    for (const [k, v] of Object.entries(progress?.wvl_state_by_eliminatoria_tier || {})) {
      wvlStateByKey.set(k, v);
    }
    const historyByKey = new Map();
    for (const [k, v] of Object.entries(progress?.history_by_eliminatoria_tier || {})) {
      historyByKey.set(k, Array.isArray(v) ? v : []);
    }
    return { passed, attemptsByKey, winsByKey, lossesByKey, avgTimesByKey, avgStateByKey, wvlStateByKey, historyByKey };
  }, [progress]);

  // Tier-aware finalist lookup ({game_id, tier_id} → qualified).
  const finalistIndex = useMemo(() => {
    const set = new Set();
    for (const q of (progress?.finalist_qualifications || [])) {
      set.add(`${q.game_id}|${q.tier_id || ""}`);
    }
    return set;
  }, [progress]);

  // Accordion: which (game, tier) section is open. Only ONE at a time.
  // Persists the LAST opened section in localStorage so when the user returns
  // from playing, it stays open and is moved to the top of the list.
  const LAST_KEY = "elim_lobby_last_section";
  const [expanded, setExpanded] = useState(() => {
    try {
      const v = localStorage.getItem(LAST_KEY);
      return v ? new Set([v]) : new Set();
    } catch (err) { console.debug("clipboard unavailable", err); return new Set(); }
  });
  useEffect(() => {
    if (groupedByGameTier.length === 0) return;
    // If nothing remembered (or the remembered key is no longer in the list),
    // auto-open the FIRST section so the user sees something immediately.
    setExpanded((prev) => {
      if (prev.size > 0) {
        const cur = Array.from(prev)[0];
        if (groupedByGameTier.some((s) => s.key === cur)) return prev; // still valid
      }
      return new Set([groupedByGameTier[0].key]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedByGameTier.length]);

  // Toggle handler: opens the section EXCLUSIVELY (closes all others) and
  // remembers it as the last-opened one.
  const toggleSection = useCallback((key) => {
    setExpanded((prev) => {
      if (prev.has(key) && prev.size === 1) {
        // Clicking the open one collapses it.
        try { localStorage.removeItem(LAST_KEY); } catch (err) { console.debug("storage unavailable", err); }
        return new Set();
      }
      try { localStorage.setItem(LAST_KEY, key); } catch (err) { console.debug("storage unavailable", err); }
      return new Set([key]);
    });
  }, []);

  // Sort sections so the LAST-opened one is always at the top.
  const orderedSections = useMemo(() => {
    let lastKey = null;
    try { lastKey = localStorage.getItem(LAST_KEY); } catch (err) { console.debug("storage unavailable", err); }
    if (!lastKey) return groupedByGameTier;
    const top = groupedByGameTier.find((s) => s.key === lastKey);
    if (!top) return groupedByGameTier;
    return [top, ...groupedByGameTier.filter((s) => s.key !== lastKey)];
  }, [groupedByGameTier, expanded]);

  // Classify each (game, tier) section into one of five buckets so we can
  // render the hybrid layout: a focused "Em curso" group at the top + a
  // collapsible "Explorar" combo-box for the rest.
  //
  //   - in_progress: user has at least one attempt but hasn't passed all elims
  //   - finalist:    user is qualified for this (game, tier)
  //   - completed:   all elims passed but not yet finalist
  //   - failed:      no more attempts left on the remaining (non-passed) elims
  //                  so the section is unreachable — treated as "done" but
  //                  with a distinct visual cue in the tier dropdown.
  //   - not_started: zero attempts on every elim of this section
  const sectionMeta = useMemo(() => {
    const meta = new Map();
    for (const sec of groupedByGameTier) {
      const tid = sec.tier?.id || "";
      const elims = sec.elims || [];
      const passed = elims.filter((e) => progressIndex.passed.has(`${e.id}|${tid}`)).length;
      const attempts = elims.reduce(
        (acc, e) => acc + (progressIndex.attemptsByKey.get(`${e.id}|${tid}`) || 0),
        0,
      );
      const isFinalist = finalistIndex.has(`${sec.game.id}|${tid}`);
      // Compute per-elim exhaustion so we can detect when the whole section
      // is unreachable. Mirrors the logic used in <EliminatoriaCard/>.
      const allBlocked = elims.length > 0 && elims.every((e) => {
        const key = `${e.id}|${tid}`;
        if (progressIndex.passed.has(key)) return true;
        const isWinsVsLosses = e.mode === "wins_vs_losses";
        const isAvgTime = e.mode === "avg_time";
        const maxAttempts = Number(e.max_attempts || 3);
        const attemptsUsed = progressIndex.attemptsByKey.get(key) || 0;
        const attemptsLeft = Math.max(0, maxAttempts - attemptsUsed);
        if (isAvgTime) {
          const st = progressIndex.avgStateByKey.get(key) || {};
          const avgCurrentOpen = !!st.current_attempt_open;
          const avgAttemptsLeft = Number(
            st.attempts_left ?? Math.max(0, maxAttempts - attemptsUsed),
          );
          return !avgCurrentOpen && avgAttemptsLeft === 0;
        }
        if (isWinsVsLosses) {
          const st = progressIndex.wvlStateByKey.get(key) || {};
          const curOpen = !!st.current_attempt_open;
          const left = Number(st.attempts_left ?? attemptsLeft);
          return !curOpen && left === 0;
        }
        return attemptsLeft === 0;
      });
      let status;
      if (isFinalist) status = "finalist";
      else if (passed === elims.length && elims.length > 0) status = "completed";
      else if (allBlocked) status = "failed";
      else if (attempts > 0 || passed > 0) status = "in_progress";
      else status = "not_started";
      meta.set(sec.key, { status, passed, total: elims.length, attempts });
    }
    return meta;
  }, [groupedByGameTier, progressIndex, finalistIndex]);

  // Search + filter chips for the new lobby UI.
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | in_progress | finalist | not_started
  // Cascading combo for "Explorar outros jogos".
  const [exploreGameId, setExploreGameId] = useState("");
  const [exploreTierId, setExploreTierId] = useState("");

  // Sections matching the current search + status filter.
  const filteredSections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return groupedByGameTier.filter((sec) => {
      const m = sectionMeta.get(sec.key) || { status: "not_started" };
      if (statusFilter !== "all" && m.status !== statusFilter) return false;
      if (q) {
        const hay = `${sec.game.name} ${sec.tier?.name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [groupedByGameTier, sectionMeta, searchQuery, statusFilter]);

  // "Os meus jogos em curso": all sections the user has touched (in_progress
  // OR finalist OR completed), respecting the current search query. These
  // appear at the top, expanded by default for the recent ones.
  const myActiveSections = useMemo(() => {
    if (statusFilter !== "all") return []; // when filtering, show only the filtered list
    return filteredSections.filter((sec) => {
      const m = sectionMeta.get(sec.key);
      return m && m.status !== "not_started";
    });
  }, [filteredSections, sectionMeta, statusFilter]);

  // Distinct games + their tiers for the "Explorar" combo box. Includes ALL
  // active games — even ones the user has already started — so the user can
  // jump straight to a specific (game, tier) section even after using filters.
  const gamesForCombo = useMemo(() => {
    const seen = new Map();
    for (const sec of groupedByGameTier) {
      if (!seen.has(sec.game.id)) {
        seen.set(sec.game.id, { game: sec.game, tiers: [] });
      }
      const entry = seen.get(sec.game.id);
      if (sec.tier && !entry.tiers.some((t) => t.id === sec.tier.id)) {
        entry.tiers.push(sec.tier);
      }
    }
    return Array.from(seen.values());
  }, [groupedByGameTier]);

  const tiersForExplore = useMemo(() => {
    const g = gamesForCombo.find((x) => x.game.id === exploreGameId);
    if (!g) return [];
    // Annotate each tier with `done` (passed → checkmark ✅) or `failed`
    // (attempts exhausted → prohibition 🚫). Both variants keep showing in
    // the dropdown but are disabled so the user knows they exist yet can no
    // longer reopen them.
    return g.tiers.map((tr) => {
      const meta = sectionMeta.get(`${g.game.id}|${tr.id || ""}`) || {};
      const done = meta.status === "finalist" || meta.status === "completed";
      const failed = meta.status === "failed";
      return { ...tr, done, failed };
    });
  }, [gamesForCombo, exploreGameId, sectionMeta]);

  // Reset the tier selector whenever the game changes.
  useEffect(() => {
    setExploreTierId("");
  }, [exploreGameId]);

  // Open the chosen (game, tier) section: replaces the expanded set with that
  // single key and persists it as the last-opened. Mimics toggleSection but
  // never collapses.
  const openExploreSection = useCallback(() => {
    if (!exploreGameId) return;
    const wantedTier = exploreTierId || "";
    const sec = groupedByGameTier.find(
      (s) => s.game.id === exploreGameId && (s.tier?.id || "") === wantedTier,
    );
    if (!sec) {
      toast.error(t("elim_lobby_combo_not_found"));
      return;
    }
    try { localStorage.setItem(LAST_KEY, sec.key); } catch (err) { console.debug("storage unavailable", err); }
    setExpanded(new Set([sec.key]));
    // Scroll into view (after re-render) so the user lands on the section.
    setTimeout(() => {
      document.querySelector(`[data-testid="elim-section-${sec.key}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [exploreGameId, exploreTierId, groupedByGameTier]);

  // Counters for the filter chips (shown as small numbers next to the label).
  const filterCounts = useMemo(() => {
    const c = { all: 0, in_progress: 0, finalist: 0, not_started: 0 };
    for (const [, m] of sectionMeta) {
      c.all += 1;
      if (m.status === "in_progress") c.in_progress += 1;
      else if (m.status === "finalist") c.finalist += 1;
      else if (m.status === "not_started") c.not_started += 1;
    }
    return c;
  }, [sectionMeta]);

  // "Continuar onde paraste" banner: shown when there's a remembered section
  const profileComplete = me && me.name && me.school && me.nationality;

  const handleProfileSaved = (updatedUser) => {
    setMe(updatedUser);
    // Sync to sessionStorage so GamePlay sees the new avatar/name/school
    // immediately when the user starts a new attempt.
    if (updatedUser?.avatar_cfg) {
      sessionStorage.setItem("player_avatar_cfg", JSON.stringify(updatedUser.avatar_cfg));
    }
    if (updatedUser?.name) sessionStorage.setItem("player_name", updatedUser.name);
    if (updatedUser?.school) sessionStorage.setItem("player_school", updatedUser.school);
    if (updatedUser?.nationality) sessionStorage.setItem("player_nationality", updatedUser.nationality);
    patchExtra({
      name: updatedUser.name,
      school: updatedUser.school,
      nationality: updatedUser.nationality,
      avatar_cfg: updatedUser.avatar_cfg,
    });
  };

  return (
    <SiteChrome>
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-16">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-violet-600 text-white p-2.5 shadow-md">
              <Crown className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-violet-700 font-bold">{t("elim_lobby_eyebrow")}</div>
              <h1 className="font-[Fredoka] text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
                {t("elim_lobby_greeting", { name: me?.name || me?.username || t("elim_lobby_default_student") })}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setProfileOpen(true)}
              variant="outline"
              className="rounded-full"
              data-testid="elim-btn-profile"
            >
              <Settings className="w-4 h-4 mr-1.5" /> {t("elim_lobby_profile_btn")}
            </Button>
            <Button
              onClick={() => { logout(); nav("/eliminatorias"); }}
              variant="ghost"
              className="rounded-full text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              data-testid="elim-btn-logout"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> {t("elim_lobby_logout_btn")}
            </Button>
          </div>
        </div>

        {/* Profile completion banner */}
        {!profileComplete && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-4 mb-6 flex items-start gap-3"
            data-testid="elim-profile-incomplete-banner"
          >
            <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-amber-900">
              <strong>{t("elim_lobby_almost_there")}</strong> {t("elim_lobby_complete_profile_hint")}
              <Button
                onClick={() => setProfileOpen(true)}
                size="sm"
                className="ml-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                data-testid="elim-banner-complete-profile"
              >
                {t("elim_lobby_complete_now")}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Finalist badges (tier-aware) */}
        {(progress?.finalist_qualifications?.length || 0) > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl bg-gradient-to-r from-amber-400 to-amber-600 text-white p-5 mb-8 shadow-lg"
            data-testid="elim-finalist-banner"
          >
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 shrink-0" />
              <div>
                <div className="font-[Fredoka] text-xl font-bold">{t("elim_lobby_you_are_finalist")}</div>
                <div className="text-sm text-amber-50/95">
                  {(() => {
                    const n = progress.finalist_qualifications.length;
                    return t("elim_lobby_finalist_categories", { n, plural: n === 1 ? t("elim_lobby_category_one") : t("elim_lobby_category_many") });
                  })()}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Profile summary card */}
        {me && (
          <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-5 mb-8 flex flex-wrap gap-4 items-center" data-testid="elim-profile-summary">
            {me.avatar_cfg ? (
              <AvatarPreview cfg={me.avatar_cfg} size={56} />
            ) : (
              <UserCircle2 className="w-12 h-12 text-violet-500 shrink-0" />
            )}
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{t("elim_lobby_account")}</div>
              <div className="font-semibold text-slate-900">{me.username} · {me.email}</div>
            </div>
            <div className="text-sm text-slate-700 flex flex-wrap gap-x-5 gap-y-1">
              <span className="flex items-center gap-1.5"><SchoolIcon className="w-4 h-4 text-slate-400" /> {me.school || <em className="text-slate-400">{t("elim_lobby_no_school")}</em>}</span>
              <span className="flex items-center gap-1.5"><Flag className="w-4 h-4 text-slate-400" /> {NATIONALITIES.find((n) => n.code === me.nationality)?.label || me.nationality || <em className="text-slate-400">{t("elim_lobby_no_country")}</em>}</span>
            </div>
          </div>
        )}

        {/* Eliminatorias list */}
        <h2 className="font-[Fredoka] text-2xl font-bold text-white mb-1">{t("elim_lobby_challenges_available")}</h2>
        <p className="text-sm text-white/70 mb-6" dangerouslySetInnerHTML={{ __html: t("elim_lobby_complete_all_to_qualify") }} />


        {loading ? (
          <div className="text-center text-slate-500 py-12" data-testid="elim-lobby-loading">{t("common_loading")}</div>
        ) : groupedByGameTier.length === 0 ? (
          <div className="rounded-3xl bg-white p-12 text-center border-2 border-dashed border-slate-200" data-testid="elim-lobby-empty">
            <Crown className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">{t("elim_lobby_empty")}</p>
          </div>
        ) : (
          <>
            {/* SEARCH + FILTER CHIPS — scale-friendly toolbar for ~15 games × 9 tiers. */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-3 md:p-4 mb-5 flex flex-col gap-3" data-testid="elim-lobby-toolbar">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("elim_lobby_search_placeholder")}
                  className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-300 text-sm"
                  data-testid="elim-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                    data-testid="elim-search-clear"
                    aria-label={t("elim_lobby_search_clear_aria")}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-0.5">
                <ListFilter className="w-4 h-4 text-slate-400 shrink-0" />
                {[
                  { key: "all", label: t("elim_lobby_filter_all"), count: filterCounts.all },
                  { key: "in_progress", label: t("elim_lobby_filter_in_progress"), count: filterCounts.in_progress },
                  { key: "finalist", label: t("elim_lobby_filter_finalist"), count: filterCounts.finalist },
                  { key: "not_started", label: t("elim_lobby_filter_not_started"), count: filterCounts.not_started },
                ].map((chip) => {
                  const active = statusFilter === chip.key;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setStatusFilter(chip.key)}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
                        active
                          ? "bg-violet-600 text-white border-violet-600"
                          : "bg-white text-slate-700 border-slate-200 hover:border-violet-300 hover:text-violet-700"
                      }`}
                      data-testid={`elim-filter-${chip.key}`}
                    >
                      {chip.label}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full tabular-nums ${
                        active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                      }`}>
                        {chip.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {(() => {
              // Renders a single section (accordion card). Extracted so we can
              // re-use the exact same UI in both the "Em curso" block and the
              // generic filtered list below — no visual divergence.
              const renderSection = ({ key, game, tier, elims }) => {
                const tierId = tier?.id || null;
                const passedForSection = elims.filter(
                  (e) => progressIndex.passed.has(`${e.id}|${tierId || ""}`)
                ).length;
                const totalForSection = elims.length;
                const isFinalist = finalistIndex.has(`${game.id}|${tierId || ""}`);
                const isOpen = expanded.has(key);
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-3xl bg-white shadow-md border border-slate-200 overflow-hidden"
                    data-testid={`elim-section-${key}`}
                  >
                    <button
                      onClick={() => toggleSection(key)}
                      className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white flex flex-wrap items-center justify-between gap-3 hover:opacity-95 transition-opacity"
                      data-testid={`elim-section-toggle-${key}`}
                    >
                      <div className="flex items-center gap-3 text-left min-w-0">
                        {isOpen ? (
                          <ChevronDown className="w-5 h-5 shrink-0" />
                        ) : (
                          <ChevronRight className="w-5 h-5 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-violet-200">{t("elim_lobby_game_label")}</span>
                            <h3 className="font-[Fredoka] text-lg font-bold leading-tight">{game.name}</h3>
                            {tier && (
                              <span
                                className="ml-1 inline-flex items-center px-3 py-1 rounded-md bg-amber-100 text-amber-900 text-sm font-bold border border-amber-300 shadow-sm"
                                data-testid={`elim-section-tier-${key}`}
                              >
                                {tier.name}{tier.level ? ` (${t("level")} ${tier.level})` : ""}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Badge
                          className={`border-white/20 hover:bg-white/25 ${
                            passedForSection === totalForSection && totalForSection > 0
                              ? "bg-emerald-500/90 text-white"
                              : "bg-white/15 text-white"
                          }`}
                          data-testid={`elim-section-progress-${key}`}
                        >
                          {passedForSection}/{totalForSection} {t("elim_lobby_passed_count")}
                        </Badge>
                        {isFinalist && (
                          <FinalistDiplomaButton
                            gameId={game.id}
                            tierId={tierId}
                            gameName={`${game.name}${tier ? ` · ${tier.name}${tier.level ? ` (${t("level")} ${tier.level})` : ""}` : ""}`}
                            variant="inline"
                          />
                        )}
                        {isFinalist && (
                          <Badge
                            className="bg-amber-400 text-amber-950 hover:bg-amber-300"
                            data-testid={`elim-finalist-badge-${key}`}
                          >
                            <Trophy className="w-3.5 h-3.5 mr-1" /> {t("elim_lobby_finalist_badge")}
                          </Badge>
                        )}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {elims.map((e, idx) => {
                          const elimKey = `${e.id}|${tierId || ""}`;
                          const passed = progressIndex.passed.has(elimKey);
                          const prev = elims[idx - 1];
                          const prevPassed = prev
                            ? progressIndex.passed.has(`${prev.id}|${tierId || ""}`)
                            : true;
                          const locked = !prevPassed;
                          const attemptsUsed = progressIndex.attemptsByKey.get(elimKey) || 0;
                          const winsCount = progressIndex.winsByKey.get(elimKey) || 0;
                          const lossesCount = progressIndex.lossesByKey.get(elimKey) || 0;
                          const avgTimes = progressIndex.avgTimesByKey.get(elimKey) || [];
                          const avgState = progressIndex.avgStateByKey.get(elimKey) || null;
                          const wvlState = progressIndex.wvlStateByKey.get(elimKey) || null;
                          const history = progressIndex.historyByKey.get(elimKey) || [];
                          return (
                            <EliminatoriaCard
                              key={elimKey}
                              e={e}
                              tierId={tierId}
                              rank={idx + 1}
                              passed={passed}
                              locked={locked && !passed}
                              attemptsUsed={attemptsUsed}
                              winsCount={winsCount}
                              lossesCount={lossesCount}
                              avgTimes={avgTimes}
                              avgState={avgState}
                              wvlState={wvlState}
                              history={history}
                            />
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                );
              };

              // When a status filter (other than "all") OR a search query is
              // active, show a single flat filtered list — no extra grouping.
              const hasFilter = statusFilter !== "all" || searchQuery.trim().length > 0;
              if (hasFilter) {
                if (filteredSections.length === 0) {
                  return (
                    <div className="rounded-3xl bg-white p-10 text-center border-2 border-dashed border-slate-200" data-testid="elim-no-results">
                      <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-bold">{t("elim_lobby_no_results_title")}</p>
                      <p className="text-slate-500 text-sm mt-1">{t("elim_lobby_no_results_hint")}</p>
                    </div>
                  );
                }
                return <div className="space-y-4" data-testid="elim-filtered-list">{filteredSections.map(renderSection)}</div>;
              }

              // Default view: "Em curso" at the top + "Explorar" combo for new picks.
              return (
                <div className="space-y-8">
                  {myActiveSections.length > 0 && (
                    <div data-testid="elim-active-block">
                      <div className="flex items-center gap-2 mb-3 px-1">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 grid place-items-center text-white shadow-sm">
                          <Sparkles className="w-4 h-4" />
                        </div>
                        <h2 className="font-[Fredoka] text-lg font-bold text-slate-900">
                          {t("elim_lobby_my_active_games")}
                        </h2>
                        <Badge className="bg-violet-100 text-violet-700 border border-violet-200">
                          {myActiveSections.length}
                        </Badge>
                      </div>
                      <div className="space-y-4">
                        {myActiveSections.map(renderSection)}
                      </div>
                    </div>
                  )}

                  {/* Cascading combo to pick any (game, tier) section, perfect for
                      scaling beyond ~10 sections without filling the viewport. */}
                  <div className="rounded-3xl bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 border-2 border-violet-200 p-5 md:p-6" data-testid="elim-explore-block">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 grid place-items-center text-white shadow-sm">
                        <Compass className="w-4 h-4" />
                      </div>
                      <h2 className="font-[Fredoka] text-lg font-bold text-slate-900">
                        {myActiveSections.length > 0 ? t("elim_lobby_explore_others") : t("elim_lobby_pick_a_game")}
                      </h2>
                    </div>
                    <p className="text-sm text-slate-600 mb-4" dangerouslySetInnerHTML={{ __html: t("elim_lobby_x_games_available", { n: gamesForCombo.length, plural: gamesForCombo.length === 1 ? t("elim_lobby_game_available_one") : t("elim_lobby_game_available_many") }) }} />
                    <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase tracking-wider">{t("elim_lobby_game_label")}</label>
                        <select
                          value={exploreGameId}
                          onChange={(e) => setExploreGameId(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 bg-white focus:outline-none focus:border-violet-400 text-sm font-medium"
                          data-testid="elim-explore-game"
                        >
                          <option value="">{t("elim_lobby_pick_game_option")}</option>
                          {gamesForCombo.map(({ game }) => (
                            <option key={game.id} value={game.id}>{game.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase tracking-wider">{t("elim_lobby_tier_label")}</label>
                        <select
                          value={exploreTierId}
                          onChange={(e) => setExploreTierId(e.target.value)}
                          disabled={!exploreGameId || tiersForExplore.length === 0}
                          className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 bg-white focus:outline-none focus:border-violet-400 text-sm font-medium disabled:bg-slate-100 disabled:text-slate-400"
                          data-testid="elim-explore-tier"
                        >
                          {tiersForExplore.length === 0 ? (
                            <option value="">{t("elim_lobby_no_tiers")}</option>
                          ) : (
                            <>
                              <option value="">{t("elim_lobby_pick_tier_option")}</option>
                              {tiersForExplore.map((tr) => {
                                const prefix = tr.done ? "✅ " : tr.failed ? "🚫 " : "";
                                const suffix = tr.done
                                  ? ` · ${t("elim_lobby_tier_done_suffix")}`
                                  : tr.failed
                                    ? ` · ${t("elim_lobby_tier_failed_suffix")}`
                                    : "";
                                return (
                                  <option key={tr.id} value={tr.id} disabled={tr.done || tr.failed}>
                                    {prefix}{tr.name}{tr.level ? ` (${t("level")} ${tr.level})` : ""}{suffix}
                                  </option>
                                );
                              })}
                            </>
                          )}
                        </select>
                      </div>
                      <div className="flex sm:items-end">
                        <Button
                          onClick={openExploreSection}
                          disabled={!exploreGameId || (tiersForExplore.length > 0 && !exploreTierId)}
                          className="w-full sm:w-auto bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white rounded-full font-bold px-6"
                          data-testid="elim-explore-open"
                        >
                          {t("elim_lobby_open_btn")}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* The opened "explored" section, when it's NOT already in the
                      "Os meus jogos em curso" block above. */}
                  {(() => {
                    const openedKey = Array.from(expanded)[0];
                    if (!openedKey) return null;
                    if (myActiveSections.some((s) => s.key === openedKey)) return null;
                    const sec = groupedByGameTier.find((s) => s.key === openedKey);
                    if (!sec) return null;
                    return <div className="space-y-4" data-testid="elim-explored-list">{renderSection(sec)}</div>;
                  })()}
                </div>
              );
            })()}
          </>
        )}

        <div className="mt-10 text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-violet-700 underline" data-testid="elim-lobby-back-home">{t("elim_lobby_back_home")}</Link>
        </div>
      </section>
      {profileOpen && me && (
        <EliminatoriasProfileDialog
          user={me}
          onClose={() => setProfileOpen(false)}
          onSaved={(u) => { handleProfileSaved(u); setProfileOpen(false); }}
        />
      )}
    </SiteChrome>
  );
}

function FinalistDiplomaButton({ gameId, tierId = null, gameName, variant = "banner" }) {
  const { lang, t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pngBlob, setPngBlob] = useState(null);

  const diplomaParams = { lang, ...(tierId ? { tier_id: tierId } : {}) };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/diplomas/finalist/${gameId}`, {
        params: diplomaParams,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeGame = String(gameName || "jogo").replace(/[^A-Za-z0-9_.-]+/g, "_");
      a.download = `diploma-finalista-${safeGame}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("elim_share_diploma_downloaded"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("elim_share_diploma_download_fail"));
    } finally {
      setBusy(false);
    }
  };

  const openShare = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/diplomas/finalist/${gameId}`, {
        params: { ...diplomaParams, format: "png" },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "image/png" });
      const safeGame = String(gameName || "jogo").replace(/[^A-Za-z0-9_.-]+/g, "_");
      const fileName = `diploma-finalista-${safeGame}.png`;
      const file = new File([blob], fileName, { type: "image/png" });
      // Prefer Web Share API (mobile native sheet → Instagram/WhatsApp/etc.)
      const text = t("elim_share_finalist_text", { game: gameName });
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare && navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            title: t("elim_share_webshare_title", { game: gameName }),
            text,
          });
          toast.success(t("elim_share_shared"));
          return;
        } catch (e) {
          if (e?.name === "AbortError") return; // user cancelled the share sheet
          // Fall through to the manual modal.
        }
      }
      // Desktop / unsupported browsers → open our manual share modal.
      setPngBlob(blob);
      setShareOpen(true);
    } catch (err) {
      toast.error(t("elim_share_prepare_error"));
    } finally {
      setBusy(false);
    }
  };

  // Inline variant — compact icon-pair to slot into the dark/violet section
  // header bar. Used between the "X/Y passadas" badge and the "Finalista"
  // badge. Identical actions to the banner variant, only the chrome differs.
  if (variant === "inline") {
    return (
      <>
        <div
          className="inline-flex items-center gap-1 rounded-full bg-white/15 border border-white/30 px-1 py-0.5 backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); download(); }}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/15 transition text-xs font-bold disabled:opacity-60 text-white"
            data-testid={`elim-finalist-diploma-inline-${gameId}`}
            title={t("elim_share_download_diploma_title")}
          >
            <Download className="w-3.5 h-3.5" /> {t("elim_share_diploma_btn")}
          </button>
          <span className="w-px h-4 bg-white/30" />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openShare(); }}
            disabled={busy}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/15 transition disabled:opacity-60 text-white"
            data-testid={`elim-finalist-share-inline-${gameId}`}
            title={t("elim_share_social_title")}
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
        </div>
        {shareOpen && pngBlob && (
          <ShareDialog
            gameName={gameName}
            pngBlob={pngBlob}
            onClose={() => { setShareOpen(false); setPngBlob(null); }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-1 py-1 backdrop-blur-sm">
        <button
          onClick={download}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-white/15 transition text-sm font-bold disabled:opacity-60"
          data-testid={`elim-finalist-diploma-${gameId}`}
          title={t("elim_share_download_diploma_title")}
        >
          <Download className="w-4 h-4" /> {t("elim_share_diploma_btn")}
        </button>
        <span className="w-px h-5 bg-white/30" />
        <button
          onClick={openShare}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-white/15 transition text-sm font-bold disabled:opacity-60"
          data-testid={`elim-finalist-share-${gameId}`}
          title={t("elim_share_social_title")}
        >
          <Share2 className="w-4 h-4" /> {t("elim_share_share_btn")}
        </button>
      </div>
      <span className="text-xs text-amber-50/90 ml-1.5">· {gameName}</span>
      {shareOpen && pngBlob && (
        <ShareDialog
          gameName={gameName}
          pngBlob={pngBlob}
          onClose={() => { setShareOpen(false); setPngBlob(null); }}
        />
      )}
    </>
  );
}

function ShareDialog({ gameName, pngBlob, onClose }) {
  const { t } = useI18n();
  const text = t("elim_share_finalist_text", { game: gameName });
  // Caption tailored for Instagram (Insta doesn't support clickable URLs in posts, but @-mentions work).
  const instagramCaption = t("elim_share_instagram_caption", { game: gameName });
  const url = typeof window !== "undefined" ? window.location.origin + "/eliminatorias" : "";
  const previewUrl = React.useMemo(() => URL.createObjectURL(pngBlob), [pngBlob]);
  React.useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);
  const [instaStep, setInstaStep] = useState(null); // null | "ready"

  const downloadPng = () => {
    const a = document.createElement("a");
    a.href = previewUrl;
    const safeGame = String(gameName || "jogo").replace(/[^A-Za-z0-9_.-]+/g, "_");
    a.download = `diploma-finalista-${safeGame}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success(t("elim_share_img_downloaded"));
  };
  const copyImage = async () => {
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
        toast.success(t("elim_share_img_copied"));
      } else {
        throw new Error("clipboard unsupported");
      }
    } catch (e) {
      toast.error(t("elim_share_img_copy_fail"));
    }
  };
  const copyCaption = () => {
    if (!navigator.clipboard) {
      toast.error(t("elim_share_clipboard_fail"));
      return;
    }
    navigator.clipboard.writeText(`${text} ${url}`).then(
      () => toast.success(t("elim_share_caption_copied")),
      () => toast.error(t("elim_share_copy_fail"))
    );
  };

  // Instagram flow: copy the @supertmatik caption, download the PNG, and show the
  // step-by-step guide. On mobile we try the native Instagram deep link too.
  const shareOnInstagram = async () => {
    // 1) Try copying the caption (with @supertmatik mention).
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(instagramCaption);
      }
    } catch (err) { console.debug("clipboard unavailable — user can copy manually below", err); }
    // 2) Download the image so it lives in the camera roll / downloads folder.
    const a = document.createElement("a");
    a.href = previewUrl;
    const safeGame = String(gameName || "jogo").replace(/[^A-Za-z0-9_.-]+/g, "_");
    a.download = `diploma-finalista-${safeGame}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    // 3) Show the guided instructions.
    setInstaStep("ready");
    // 4) On mobile, try to open the Instagram camera (works on iOS Safari / Android Chrome).
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
    if (isMobile) {
      // Best-effort deep link (Instagram won't pre-fill content but takes the user to the app).
      window.location.href = "instagram://camera";
      setTimeout(() => {
        // Fallback if the deep link didn't open the app.
        if (document.hidden) return;
      }, 800);
    }
  };

  const openWhatsApp = () => {
    const msg = encodeURIComponent(`${text} ${url}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank", "noopener,noreferrer");
  };
  const openX = () => {
    const tweet = encodeURIComponent(text);
    window.open(`https://twitter.com/intent/tweet?text=${tweet}&url=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
  };
  const openFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[90] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4" data-testid="elim-share-dialog" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-400 to-amber-600 text-white sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            <h2 className="font-[Fredoka] text-xl font-bold">{t("elim_share_dialog_title")}</h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/15 rounded-full p-1.5" data-testid="elim-share-close">
            ✕
          </button>
        </div>
        <div className="p-6">
          <img
            src={previewUrl}
            alt={t("elim_share_preview_alt")}
            className="w-full rounded-xl border border-slate-200 shadow-sm mb-5 bg-slate-100"
            style={{ aspectRatio: "1.414 / 1" }}
            data-testid="elim-share-preview"
          />

          {/* Featured: Instagram (matches @supertmatik handle) */}
          <button
            onClick={shareOnInstagram}
            className="w-full mb-2 flex items-center justify-center gap-2 px-4 py-3 rounded-full text-white font-bold text-sm shadow-md hover:shadow-lg transition-all"
            style={{
              background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
            }}
            data-testid="elim-share-instagram-tag"
          >
            <Instagram className="w-4 h-4" />
            {t("elim_share_tag_instagram")}
          </button>

          {instaStep === "ready" && (
            <div className="mb-4 rounded-2xl border-2 border-pink-200 bg-pink-50 p-4" data-testid="elim-share-instagram-steps">
              <div className="flex items-center gap-2 mb-2">
                <Instagram className="w-5 h-5 text-pink-600" />
                <span className="font-[Fredoka] font-bold text-pink-700 text-sm">{t("elim_share_all_ready")}</span>
              </div>
              <ol className="text-xs text-slate-700 list-decimal pl-5 space-y-1">
                <li dangerouslySetInnerHTML={{ __html: t("elim_share_step1") }} />
                <li dangerouslySetInnerHTML={{ __html: t("elim_share_step2") }} />
                <li dangerouslySetInnerHTML={{ __html: t("elim_share_step3") }} />
                <li dangerouslySetInnerHTML={{ __html: t("elim_share_step4") }} />
                <li dangerouslySetInnerHTML={{ __html: t("elim_share_step5") }} />
              </ol>
              <button
                onClick={async () => {
                  try { await navigator.clipboard.writeText(instagramCaption); toast.success(t("elim_share_caption_copied_again")); }
                  catch (_) { toast.error(t("elim_share_copy_fail_manual")); }
                }}
                className="mt-3 text-xs font-bold text-pink-700 hover:text-pink-900 underline"
                data-testid="elim-share-instagram-copy-again"
              >
                {t("elim_share_copy_again")}
              </button>
              <details className="mt-2">
                <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-700">{t("elim_share_view_caption")}</summary>
                <pre className="mt-1 text-[11px] text-slate-700 bg-white p-2 rounded border border-pink-100 whitespace-pre-wrap" data-testid="elim-share-instagram-caption-preview">{instagramCaption}</pre>
              </details>
            </div>
          )}

          <p className="text-xs text-slate-500 mb-3" dangerouslySetInnerHTML={{ __html: t("elim_share_info") }} />
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={downloadPng}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold text-sm transition"
              data-testid="elim-share-download-png"
            >
              <Download className="w-4 h-4" /> {t("elim_share_download_img")}
            </button>
            <button
              onClick={copyImage}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-bold text-sm transition"
              data-testid="elim-share-copy-image"
            >
              <Crown className="w-4 h-4" /> {t("elim_share_copy_img")}
            </button>
            <button
              onClick={openWhatsApp}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm transition"
              data-testid="elim-share-whatsapp"
            >
              WhatsApp
            </button>
            <button
              onClick={openX}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition"
              data-testid="elim-share-twitter"
            >
              X / Twitter
            </button>
            <button
              onClick={openFacebook}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition"
              data-testid="elim-share-facebook"
            >
              Facebook
            </button>
            <button
              onClick={copyCaption}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-900 font-bold text-sm transition"
              data-testid="elim-share-copy-caption"
            >
              {t("elim_share_copy_caption_btn")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EliminatoriaCard({ e, rank, passed, locked, attemptsUsed = 0, winsCount = 0, lossesCount = 0, avgTimes = [], avgState = null, wvlState = null, tierId = null, history = [] }) {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [starting, setStarting] = useState(false);
  const isVsRobot = e.mode === "vencer_robot";
  const isWinsVsLosses = e.mode === "wins_vs_losses";
  const isAvgTime = e.mode === "avg_time";
  const targetTime = Number(e.target_time || 0);
  const totalGames = Number(e.total_games || 5);
  const winsRequired = Number(e.wins_required || 3);
  const maxAttempts = Number(e.max_attempts || 3);
  const attemptsLeft = Math.max(0, maxAttempts - attemptsUsed);
  // WINS-VS-LOSSES (v2, 2026-02): each attempt is a group of N sub-games.
  //   - attemptsUsed counts DISTINCT attempt groups.
  //   - The card only exhausts when all M attempts are used AND the last
  //     one has closed (reached target / unreachable / full). While the
  //     current group is still open the player keeps playing sub-games.
  const wvlCurrentOpen   = !!(isWinsVsLosses && wvlState?.current_attempt_open);
  const wvlAttemptsUsed  = isWinsVsLosses ? Number(wvlState?.attempts_used ?? attemptsUsed) : attemptsUsed;
  const wvlAttemptsLeft  = isWinsVsLosses ? Number(wvlState?.attempts_left ?? Math.max(0, maxAttempts - wvlAttemptsUsed)) : attemptsLeft;
  const wvlStillPossible = !isWinsVsLosses || wvlCurrentOpen || wvlAttemptsLeft > 0;
  // For avg_time (v2, 2026-02): an "attempt" is a full run of N sub-games.
  //   - attemptsUsed already counts DISTINCT attempt groups.
  //   - Only exhaust the card when we've used all M attempts AND the last
  //     one has closed (failed / passed). While the current group is still
  //     open the player can just keep playing more sub-games.
  const avgCurrentOpen  = !!(isAvgTime && avgState?.current_attempt_open);
  const avgLastFailed   = !!(isAvgTime && avgState?.current_attempt_failed);
  const avgAttemptsUsed = isAvgTime ? Number(avgState?.attempts_used ?? attemptsUsed) : attemptsUsed;
  const avgAttemptsLeft = isAvgTime ? Number(avgState?.attempts_left ?? Math.max(0, maxAttempts - avgAttemptsUsed)) : attemptsLeft;
  const avgCurTimes     = isAvgTime ? (avgState?.current_attempt_times || avgTimes || []) : [];
  const avgCurAvg       = avgCurTimes.length > 0 ? (avgCurTimes.reduce((s, x) => s + Number(x || 0), 0) / avgCurTimes.length) : null;
  const avgCurGameNum   = avgCurTimes.length;
  const avgStillPossible = !isAvgTime || avgCurrentOpen || avgAttemptsLeft > 0;
  const exhausted = !passed && (
    (isAvgTime
      ? (!avgCurrentOpen && avgAttemptsLeft === 0)
      : isWinsVsLosses
        ? (!wvlCurrentOpen && wvlAttemptsLeft === 0)
        : attemptsLeft === 0)
    || !wvlStillPossible
    || !avgStillPossible
  );
  const robot = e.target_robot;

  const startPlay = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const params = { lang };
      if (tierId) params.tier_id = tierId;
      const { data } = await api.post(`/eliminatorias/${e.id}/play/start`, null, { params });
      // Remember which (game, tier) section the player was last on, so when
      // they come back from the game it stays open and pinned to the top.
      try {
        localStorage.setItem("elim_lobby_last_section", `${e.game_id}|${tierId || ""}`);
      } catch (err) { console.debug("storage unavailable", err); }
      // Persist the difficulty level (resolved from the chosen tier) so the
      // game page picks the right roulette / question difficulty.
      const resolvedLevel = Number(data?.level || data?.eliminatoria?.level || 1) || 1;
      sessionStorage.setItem("player_level", String(resolvedLevel));
      // Persist tier_id so GamePlay can show the exact tier label (two tiers
      // can share the same level — e.g. Escalão 6 and Escalão 9 at Nível 4).
      try {
        if (tierId) sessionStorage.setItem("player_tier_id", tierId);
        else sessionStorage.removeItem("player_tier_id");
      } catch (err) { console.debug("storage unavailable", err); }
      if (data?.eliminatoria?.mode === "vencer_robot" && data?.eliminatoria?.target_robot) {
        // Hybrid flow: run the existing VS Robot duel, but record the elim attempt.
        const robotId = data.eliminatoria.target_robot.id;
        const attemptId = data.attempt_id || (data.session_id || "").replace(/^elim-/, "");
        const usp = new URLSearchParams();
        usp.set("mode", "vs_robot");
        usp.set("robot", robotId);
        usp.set("elim", e.id);
        usp.set("elim_attempt", attemptId);
        if (tierId) usp.set("tier", tierId);
        usp.set("level", String(resolvedLevel));
        nav(`/jogar/${e.game_id}?${usp.toString()}`);
        return;
      }
      // Default flow (target_time): stash session for GamePlay to pick up.
      sessionStorage.setItem("elim_session", JSON.stringify(data));
      const usp = new URLSearchParams();
      usp.set("mode", "eliminatoria");
      usp.set("elim", e.id);
      if (tierId) usp.set("tier", tierId);
      usp.set("level", String(resolvedLevel));
      nav(`/jogar/${e.game_id}?${usp.toString()}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("elim_lobby_start_error"));
      setStarting(false);
    }
  };

  return (
    <div
      className={`relative rounded-2xl border-2 p-4 transition-all ${
        passed ? "bg-emerald-50 border-emerald-300" :
        locked ? "bg-slate-50 border-slate-200 opacity-70" :
        isVsRobot ? "bg-white border-sky-200 hover:border-sky-400 hover:shadow-md" :
        isWinsVsLosses ? "bg-white border-amber-200 hover:border-amber-400 hover:shadow-md" :
        isAvgTime ? "bg-white border-cyan-200 hover:border-cyan-400 hover:shadow-md" :
        "bg-white border-violet-200 hover:border-violet-400 hover:shadow-md"
      }`}
      data-testid={`elim-card-${e.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${
            isVsRobot ? "text-sky-700" :
            isWinsVsLosses ? "text-amber-700" :
            isAvgTime ? "text-cyan-700" :
            "text-violet-700"
          }`}>
            {t("elim_card_label")} #{rank}{
              isVsRobot ? ` · ${t("elim_card_mode_vs_robot")}` :
              isWinsVsLosses ? ` · ${t("elim_card_mode_wvl")}` :
              isAvgTime ? ` · ${t("elim_card_mode_avg")}` : ""
            }
          </div>
          <h4 className="font-[Fredoka] font-bold text-slate-900 leading-tight">{e.name}</h4>
        </div>
        {passed ? (
          <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
        ) : locked ? (
          <Lock className="w-5 h-5 text-slate-400 shrink-0" />
        ) : isVsRobot ? (
          <Bot className="w-5 h-5 text-sky-500 shrink-0" />
        ) : isWinsVsLosses ? (
          <Swords className="w-5 h-5 text-amber-500 shrink-0" />
        ) : isAvgTime ? (
          <Timer className="w-5 h-5 text-cyan-500 shrink-0" />
        ) : (
          <Crown className="w-5 h-5 text-violet-500 shrink-0" />
        )}
      </div>

      {isVsRobot && robot && (
        <div className="mt-3 flex items-center gap-2 bg-sky-50 border border-sky-100 rounded-xl px-2.5 py-2">
          <img
            src={robot.image_url}
            alt={robot.name}
            className="w-10 h-10 rounded-lg object-cover shrink-0"
            data-testid={`elim-card-robot-img-${e.id}`}
          />
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm text-slate-900 truncate" data-testid={`elim-card-robot-name-${e.id}`}>
              {robot.name}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {robot.alcunha ? `"${robot.alcunha}" · ` : ""}{t("elim_card_mental")}: <strong>{robot.mental_strength}/10</strong>
            </div>
          </div>
        </div>
      )}

      {isWinsVsLosses && (
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`elim-card-wl-scoreboard-${e.id}`}>
          <div className="flex flex-col items-center bg-emerald-50 border border-emerald-100 rounded-xl py-1.5">
            <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">{t("elim_card_wins")}</span>
            <span className="font-[Fredoka] text-lg font-bold text-emerald-800" data-testid={`elim-card-wins-${e.id}`}>
              {Number(wvlState?.wins_in_attempt ?? 0)}
            </span>
          </div>
          <div className="flex flex-col items-center bg-rose-50 border border-rose-100 rounded-xl py-1.5">
            <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wide">{t("elim_card_losses")}</span>
            <span className="font-[Fredoka] text-lg font-bold text-rose-800" data-testid={`elim-card-losses-${e.id}`}>
              {Number(wvlState?.losses_in_attempt ?? 0)}
            </span>
          </div>
        </div>
      )}

      {isAvgTime && (
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`elim-card-avg-scoreboard-${e.id}`}>
          <div className={`flex flex-col items-center rounded-xl py-1.5 border ${
            avgCurAvg !== null && avgCurAvg <= targetTime ? "bg-emerald-50 border-emerald-100" : "bg-amber-50 border-amber-100"
          }`}>
            <span className={`text-[10px] font-bold uppercase tracking-wide ${
              avgCurAvg !== null && avgCurAvg <= targetTime ? "text-emerald-700" : "text-amber-700"
            }`}>{t("elim_card_games_avg")}</span>
            <span className="font-[Fredoka] text-sm font-bold font-mono leading-tight" data-testid={`elim-card-avg-current-${e.id}`}>
              {avgCurGameNum}/{totalGames}
              {avgCurAvg !== null && (
                <span className="text-slate-500 font-normal"> · {avgCurAvg.toFixed(1)}s</span>
              )}
            </span>
          </div>
          <div className="flex flex-col items-center bg-violet-50 border border-violet-100 rounded-xl py-1.5">
            <span className="text-[10px] font-bold text-violet-700 uppercase tracking-wide">{t("elim_card_target")}</span>
            <span className="font-[Fredoka] text-lg font-bold text-violet-800 font-mono" data-testid={`elim-card-avg-target-${e.id}`}>
              {targetTime.toFixed(0)}s
            </span>
          </div>
        </div>
      )}
      {isAvgTime && avgLastFailed && !passed && avgAttemptsLeft > 0 && (
        <div className="mt-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 text-center" data-testid={`elim-card-avg-attempt-failed-${e.id}`}>
          {t("elim_card_attempt_failed", { left: avgAttemptsLeft })}
        </div>
      )}

      {(() => {
        const availableAttempts = isAvgTime ? avgAttemptsLeft : isWinsVsLosses ? wvlAttemptsLeft : attemptsLeft;
        const objectiveText = isVsRobot
          ? t("elim_obj_robot", { name: robot?.name || "—" })
          : isWinsVsLosses
            ? t("elim_obj_wvl", { wins: winsRequired, total: totalGames })
            : isAvgTime
              ? t("elim_obj_avg", { target: Number(targetTime).toFixed(0), games: totalGames })
              : t("elim_obj_target", { seconds: Number(targetTime).toFixed(0) });
        return (
          <div className="mt-3 space-y-1.5 text-xs" data-testid={`elim-card-info-${e.id}`}>
            <div className="flex gap-1.5">
              <span className="font-bold text-slate-500 shrink-0">{t("elim_card_objective_label")}:</span>
              <span className="text-slate-700" data-testid={`elim-card-objective-${e.id}`}>{objectiveText}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span>
                <span className="font-bold text-slate-500">{t("elim_card_attempts_total_label")}:</span>{" "}
                <span className="text-slate-700" data-testid={`elim-card-attempts-total-${e.id}`}>{maxAttempts}</span>
              </span>
              <span>
                <span className="font-bold text-slate-500">{t("elim_card_attempts_avail_label")}:</span>{" "}
                <span className={`font-bold ${availableAttempts > 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`elim-card-attempts-avail-${e.id}`}>{availableAttempts}</span>
              </span>
            </div>
          </div>
        );
      })()}
      {history.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`elim-card-history-${e.id}`}>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">{t("elim_hist_title")}</div>
          {isVsRobot ? (
            (() => {
              const wonAttempt = history.find((h) => h.passed);
              return wonAttempt ? (
                <div className="text-xs font-semibold text-emerald-700" data-testid={`elim-hist-robot-${e.id}`}>
                  🏆 {t("elim_hist_robot_won", { n: wonAttempt.n })}
                </div>
              ) : (
                <div className="text-xs font-semibold text-slate-500" data-testid={`elim-hist-robot-${e.id}`}>
                  {t("elim_hist_robot_not_yet")}
                </div>
              );
            })()
          ) : (
            <ul className="space-y-1">
              {history.map((h) => (
                <li key={h.n} className="flex items-center justify-between gap-2 text-xs" data-testid={`elim-hist-row-${e.id}-${h.n}`}>
                  <span className="text-slate-500 font-medium shrink-0">{t("elim_hist_attempt", { n: h.n })}</span>
                  <span className={`font-mono font-semibold text-right ${h.passed ? "text-emerald-600" : "text-slate-700"}`}>
                    {isWinsVsLosses
                      ? `${h.wins}${t("elim_hist_win_abbr")} / ${h.losses}${t("elim_hist_loss_abbr")}`
                      : isAvgTime
                        ? (h.times && h.times.length
                            ? `${h.times.map((x) => `${Number(x).toFixed(0)}s`).join(", ")}${h.avg != null ? ` · ${Number(h.avg).toFixed(1)}s` : ""}`
                            : "—")
                        : (h.won && h.time_seconds != null ? `${Number(h.time_seconds).toFixed(0)}s` : "✗")}
                    {h.passed ? " ✓" : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="mt-4">
        {passed ? (
          <div className="text-center text-xs font-bold text-emerald-700 py-1.5 bg-emerald-100 rounded-full" data-testid={`elim-status-passed-${e.id}`}>
            ✓ {t("elim_card_status_passed")}
          </div>
        ) : locked ? (
          <div className="text-center text-xs font-bold text-slate-500 py-1.5 bg-slate-100 rounded-full" data-testid={`elim-status-locked-${e.id}`}>
            🔒 {t("elim_card_status_locked")}
          </div>
        ) : exhausted ? (
          <div className="text-center text-xs font-bold text-rose-700 py-1.5 bg-rose-100 rounded-full" data-testid={`elim-status-exhausted-${e.id}`}>
            ⛔ {t("elim_card_status_exhausted")}
          </div>
        ) : (
          <Button
            onClick={startPlay}
            disabled={starting}
            className={`w-full rounded-full text-white h-9 font-bold disabled:opacity-100 ${
              isVsRobot ? "bg-sky-600 hover:bg-sky-700" :
              isWinsVsLosses ? "bg-amber-600 hover:bg-amber-700" :
              isAvgTime ? "bg-cyan-600 hover:bg-cyan-700" :
              "bg-violet-600 hover:bg-violet-700"
            }`}
            data-testid={`elim-play-${e.id}`}
          >
            {starting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("elim_card_starting")}
              </span>
            ) : (
              t("elim_card_start_game")
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
